import {
  $createCodeNode,
  $isCodeNode,
  getDefaultCodeLanguage,
  getCodeLanguages,
} from '@lexical/code';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
} from '@lexical/rich-text';
import { $wrapNodes, $isAtNodeEnd } from '@lexical/selection';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { makeStyles } from '@material-ui/styles';
import cn from 'classnames';
import {
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $getNodeByKey,
  LexicalEditor,
  RangeSelection,
  BaseSelection,
} from 'lexical';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { COLORS } from '../../../theme/variables';
import { isMobile } from '../../../utils/functions';
import Button from '../../common/button';

const useToolbarStyles = makeStyles({
  toolbar: {
    display: 'flex',
    marginBottom: '1px',
    background: 'rgb(245, 245, 245)',
    padding: '4px',
    verticalAlign: 'middle',
    borderBottom: '1px solid #BEC6D3',

    '& button.toolbar-item': {
      border: 0,
      display: 'flex',
      background: 'none',
      borderRadius: '10px',
      padding: '5px 8px',
      cursor: 'pointer',
      verticalAlign: 'middle',
    },

    '& button.toolbar-item:disabled': {
      cursor: 'not-allowed',
    },

    '& button.toolbar-item.spaced': {
      marginRight: '2px',
    },

    '& button.toolbar-item i.format': {
      backgroundSize: 'contain',
      display: 'inline-block',
      height: '18px',
      width: '18px',
      marginTop: '2px',
      verticalAlign: '-0.25em',
      opacity: 0.6,
    },

    '& button.toolbar-item:disabled i.format': {
      opacity: 0.2,
    },

    '& button.toolbar-item.active': {
      backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_30,
    },

    '& button.toolbar-item.active i': {
      opacity: 1,
    },

    '& .toolbar-item:hover:not([disabled])': {
      backgroundColor: '#eee',
    },

    '& .divider': {
      width: '1px',
      backgroundColor: '#eee',
      margin: '0 4px',
    },

    '& select.toolbar-item': {
      border: 0,
      display: 'flex',
      background: 'none',
      borderRadius: '10px',
      padding: '8px',
      verticalAlign: 'middle',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      width: '70px',
      fontSize: '14px',
      color: '#777',
      textOverflow: 'ellipsis',
    },

    '& select.code-language': {
      textTransform: 'capitalize',
      width: '130px',
    },

    '& .toolbar-item .text': {
      display: 'flex',
      lineHeight: '20px',
      width: '100px',
      verticalAlign: 'middle',
      fontSize: '14px',
      color: '#777',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      height: '20px',
      textAlign: 'left',
    },

    '& .toolbar-item .icon': {
      display: 'flex',
      width: '20px',
      height: '20px',
      userSelect: 'none',
      marginRight: '8px',
      lineHeight: '16px',
      backgroundSize: 'contain',
    },

    '& i.chevron-down': {
      marginTop: '3px',
      width: '16px',
      height: '16px',
      display: 'flex',
      userSelect: 'none',
      backgroundColor: 'transparent',
      backgroundSize: 'contain',
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/chevron-down.svg)`,
    },

    '& i.chevron-down.inside': {
      width: '16px',
      height: '16px',
      display: 'flex',
      marginLeft: '-25px',
      marginTop: '11px',
      marginRight: '10px',
      pointerEvents: 'none',
    },

    '& #block-controls button:hover': {
      backgroundColor: '#efefef',
    },

    '& #block-controls button:focus-visible': {
      borderColor: 'blue',
    },

    '& #block-controls span.block-type': {
      backgroundSize: 'contain',
      display: 'block',
      width: '18px',
      height: '18px',
      margin: '2px',
    },

    '& #block-controls span.block-type.paragraph': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/text-paragraph.svg)`,
    },

    '& #block-controls span.block-type.h1': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/type-h1.svg)`,
    },

    '& #block-controls span.block-type.h2': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/type-h2.svg)`,
    },

    '& #block-controls span.block-type.quote': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/chat-square-quote.svg)`,
    },

    '& #block-controls span.block-type.ul': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/list-ul.svg)`,
    },

    '& #block-controls span.block-type.ol': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/list-ol.svg)`,
    },

    '& #block-controls span.block-type.code': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/code.svg)`,
    },

    '& i.undo': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/arrow-counterclockwise.svg)`,
    },

    '& i.redo': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/arrow-clockwise.svg)`,
    },

    '& .icon.paragraph': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/text-paragraph.svg)`,
    },

    '& .icon.large-heading, .icon.h1': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/type-h1.svg)`,
    },

    '& .icon.small-heading, .icon.h2': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/type-h2.svg)`,
    },

    '& .icon.bullet-list, .icon.ul': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/list-ul.svg)`,
    },

    '& .icon.numbered-list, .icon.ol': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/list-ol.svg)`,
    },

    '& .icon.quote': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/chat-square-quote.svg)`,
    },

    '& .icon.code': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/code.svg)`,
    },

    '& i.bold': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/type-bold.svg)`,
    },

    '& i.italic': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/type-italic.svg)`,
    },

    '& i.underline': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/type-underline.svg)`,
    },

    '& i.strikethrough': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/type-strikethrough.svg)`,
    },

    '& i.code': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/code.svg)`,
    },

    '& i.link': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/link.svg)`,
    },

    '& i.left-align': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/text-left.svg)`,
    },

    '& i.center-align': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/text-center.svg)`,
    },

    '& i.right-align': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/text-right.svg)`,
    },

    '& i.justify-align': {
      backgroundImage: `url(${process.env.PUBLIC_URL}/editor-icons/justify.svg)`,
    },
  },
});

const useDropdownStyles = makeStyles({
  dropdown: {
    zIndex: 1000,
    display: 'block',
    position: 'absolute',
    boxShadow:
      '0 12px 28px 0 rgba(0, 0, 0, 0.2), 0 2px 4px 0 rgba(0, 0, 0, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.5)',
    borderRadius: '8px',
    minWidth: '100px',
    minHeight: '40px',
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    padding: '6px 0',

    '& .item': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      gap: '8px',
      padding: '8px 16px',
      color: '#050505',
      cursor: 'pointer',
      lineHeight: '16px',
      fontSize: '15px',
      alignContent: 'center',
      flexDirection: 'row',
      flexShrink: 0,
      backgroundColor: '#fff',
      border: 0,
      minWidth: '268px',

      '& .active': {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: COLORS.COLOR_BLUE_BASE,
      },

      '&:hover': {
        backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_30,
      },

      '& .text': {
        display: 'flex',
        lineHeight: '20px',
        alignItems: 'center',
        flexGrow: 1,
        width: '200px',
      },

      '& .icon': {
        display: 'flex',
        width: '16px',
        height: '16px',
        userSelect: 'none',
        lineHeight: '16px',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      },

      '& .icon.paragraph': {
        backgroundImage: 'url(editor-icons/text-paragraph.svg)',
      },
      '& .icon.large-heading, & .icon.h1': {
        backgroundImage: 'url(editor-icons/type-h1.svg)',
      },
      '& .icon.small-heading, & .icon.h2': {
        backgroundImage: 'url(editor-icons/type-h2.svg)',
      },
      '& .icon.bullet-list, & .icon.ul': {
        backgroundImage: 'url(editor-icons/list-ul.svg)',
      },
      '& .icon.numbered-list, & .icon.ol': {
        backgroundImage: 'url(editor-icons/list-ol.svg)',
      },
      '& .icon.quote': {
        backgroundImage: 'url(editor-icons/chat-square-quote.svg)',
      },
      '& .icon.code': {
        backgroundImage: 'url(editor-icons/code.svg)',
      },
    },
  },
});

const useLinkEditorStyles = makeStyles({
  linkEditor: {
    position: 'absolute',
    zIndex: 100,
    top: '-10000px',
    left: '-10000px',
    marginTop: '-6px',
    maxWidth: '300px',
    width: '100%',
    opacity: 0,
    backgroundColor: '#fff',
    boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
    transition: 'opacity 0.5s',

    '& .link-input': {
      display: 'block',
      width: 'calc(100% - 24px)',
      boxSizing: 'border-box',
      margin: '8px 12px',
      padding: '8px 12px',
      borderRadius: '15px',
      backgroundColor: '#eee',
      fontSize: '15px',
      color: 'rgb(5, 5, 5)',
      border: '1px solid transparent',
      outline: 0,
      position: 'relative',
      fontFamily: 'inherit',
    },

    // Save button styles are defined below; buttons container controls spacing

    '& .buttons': {
      display: 'flex',
      gap: '8px',
      margin: '0 12px 12px 12px',
    },

    '& div.link-edit': {
      backgroundImage: 'url(editor-icons/pencil-fill.svg)',
      backgroundSize: '16px',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      width: '35px',
      verticalAlign: '-0.25em',
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      cursor: 'pointer',
    },

    '& .link-input a': {
      color: 'rgb(33, 111, 219)',
      textDecoration: 'none',
      display: 'block',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      marginRight: '30px',
      textOverflow: 'ellipsis',
    },

    '& .link-input a:hover': {
      textDecoration: 'underline',
    },

    '& .button': {
      width: '20px',
      height: '20px',
      display: 'inline-block',
      padding: '6px',
      borderRadius: '8px',
      cursor: 'pointer',
      margin: '0 2px',
    },

    '& .button.hovered': {
      width: '20px',
      height: '20px',
      display: 'inline-block',
      backgroundColor: '#eee',
    },

    '& .button i, .actions i': {
      backgroundSize: 'contain',
      display: 'inline-block',
      height: '20px',
      width: '20px',
      verticalAlign: '-0.25em',
    },

    '& .link-input.error': {
      border: '1px solid #d32f2f',
      backgroundColor: '#fff5f5',
    },
  },
});

const LowPriority = 1;

const KNOWN_BLOCK_TYPES = new Set([
  'paragraph',
  'quote',
  'code',
  'h1',
  'h2',
  'ul',
  'ol',
]);

const blockTypeToBlockName = {
  code: 'Code Block',
  h1: 'Large Heading',
  h2: 'Small Heading',
  h3: 'Heading',
  h4: 'Heading',
  h5: 'Heading',
  ol: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
  ul: 'Bulleted List',
};

function Divider() {
  return <div className='divider' />;
}

function positionEditorElement(editor: HTMLDivElement, rect: DOMRect | null) {
  if (rect === null) {
    editor.style.opacity = '0';
    editor.style.top = '-1000px';
    editor.style.left = '-1000px';
  } else {
    editor.style.opacity = '1';
    editor.style.top = `${rect.top + rect.height + window.pageYOffset + 10}px`;
    editor.style.left = `${
      rect.left + window.pageXOffset - editor.offsetWidth / 2 + rect.width / 2
    }px`;
  }
}

function FloatingLinkEditor({
  editor,
  onRequestClose,
}: {
  editor: LexicalEditor;
  onRequestClose: () => void;
}) {
  const classes = useLinkEditorStyles();
  const editorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mouseDownRef = useRef(false);
  const [linkUrl, setLinkUrl] = useState('https://');
  const [isInvalid, setIsInvalid] = useState(false);
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(
    null,
  );

  const isValidLink = useCallback((value: string): boolean => {
    const trimmed = (value || '').trim();
    if (trimmed.length === 0) return false;
    const emailRegex = /^(mailto:)?[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (emailRegex.test(trimmed)) return true;
    try {
      // Add protocol if missing
      const hasProtocol = /^(https?:|mailto:|tel:)/i.test(trimmed);
      const url = new URL(hasProtocol ? trimmed : `https://${trimmed}`);
      return !!url.hostname;
    } catch {
      return false;
    }
  }, []);

  const handleSave = useCallback(() => {
    const ok = isValidLink(linkUrl);
    if (!ok) {
      setIsInvalid(true);
      return;
    }
    if (lastSelection !== null && linkUrl !== '') {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
      onRequestClose && onRequestClose();
    }
  }, [editor, isValidLink, lastSelection, linkUrl, onRequestClose]);

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl('https://');
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      nativeSelection &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const domRange = nativeSelection.getRangeAt(0);
      let rect;
      if (nativeSelection.anchorNode === rootElement) {
        let inner: HTMLElement | Element = rootElement;
        while (inner.firstElementChild != null) {
          inner = inner.firstElementChild;
        }
        rect = inner.getBoundingClientRect();
      } else {
        rect = domRange.getBoundingClientRect();
      }

      if (!mouseDownRef.current) {
        positionEditorElement(editorElem, rect);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== 'link-input') {
      positionEditorElement(editorElem, null);
      setLastSelection(null);
      setLinkUrl('https://');
      onRequestClose && onRequestClose();
    }

    return true;
  }, [editor, onRequestClose]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return true;
        },
        LowPriority,
      ),
    );
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div ref={editorRef} className={classes.linkEditor}>
      {
        <>
          <input
            ref={inputRef}
            className={`link-input${isInvalid ? ' error' : ''}`}
            value={linkUrl}
            onChange={(event) => {
              setLinkUrl(event.target.value);
              if (isInvalid) setIsInvalid(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSave();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                onRequestClose && onRequestClose();
              }
            }}
          />
          <div className='buttons'>
            <Button
              size='small'
              variant='contained'
              onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
              onClick={handleSave}
              type='button'>
              Save
            </Button>
            <Button
              size='small'
              variant='outlined'
              onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
              onClick={() => onRequestClose && onRequestClose()}
              type='button'>
              Cancel
            </Button>
          </div>
        </>
      }
    </div>
  );
}

function Select({
  onChange,
  className,
  options,
  value,
  disabled,
}: {
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  className: string;
  options: string[];
  value: string;
  disabled: boolean;
}) {
  return (
    <select
      className={className}
      onChange={onChange}
      value={value}
      disabled={disabled}>
      <option hidden={true} value='' />
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function getSelectedNode(selection: RangeSelection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

function BlockOptionsDropdownList({
  editor,
  blockType,
  toolbarRef,
  setShowBlockOptionsDropDown,
  codeStyle,
}: {
  editor: LexicalEditor;
  blockType: string;
  toolbarRef: React.RefObject<HTMLElement>;
  setShowBlockOptionsDropDown: (show: boolean) => void;
  codeStyle: boolean;
}) {
  const classes = useDropdownStyles();
  const dropDownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const toolbar = toolbarRef.current;
    const dropDown = dropDownRef.current;

    if (toolbar !== null && dropDown !== null) {
      const { top, left } = toolbar.getBoundingClientRect();
      dropDown.style.top = `${top + 40}px`;
      dropDown.style.left = `${left}px`;
    }
  }, [dropDownRef, toolbarRef]);

  useEffect(() => {
    const dropDown = dropDownRef.current;
    const toolbar = toolbarRef.current;

    if (dropDown !== null && toolbar !== null) {
      const handle = (event: Event) => {
        const target = event.target;

        if (
          target &&
          !dropDown.contains(target as Node) &&
          !toolbar.contains(target as Node)
        ) {
          setShowBlockOptionsDropDown(false);
        }
      };
      document.addEventListener('click', handle);

      return () => {
        document.removeEventListener('click', handle);
      };
    }
  }, [dropDownRef, setShowBlockOptionsDropDown, toolbarRef]);

  const formatParagraph = () => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createParagraphNode());
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatLargeHeading = () => {
    if (blockType !== 'h1') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode('h1'));
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatSmallHeading = () => {
    if (blockType !== 'h2') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode('h2'));
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatBulletList = () => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatNumberedList = () => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createQuoteNode());
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatCode = () => {
    if (blockType !== 'code') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createCodeNode());
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  return (
    <div className={classes.dropdown} ref={dropDownRef}>
      <button className='item' onClick={formatParagraph}>
        <span className='icon paragraph' />
        <span className='text'>Normal</span>
        {blockType === 'paragraph' && <span className='active' />}
      </button>
      <button className='item' onClick={formatLargeHeading}>
        <span className='icon large-heading' />
        <span className='text'>Large Heading</span>
        {blockType === 'h1' && <span className='active' />}
      </button>
      <button className='item' onClick={formatSmallHeading}>
        <span className='icon small-heading' />
        <span className='text'>Small Heading</span>
        {blockType === 'h2' && <span className='active' />}
      </button>
      <button className='item' onClick={formatBulletList}>
        <span className='icon bullet-list' />
        <span className='text'>Bullet List</span>
        {blockType === 'ul' && <span className='active' />}
      </button>
      <button className='item' onClick={formatNumberedList}>
        <span className='icon numbered-list' />
        <span className='text'>Numbered List</span>
        {blockType === 'ol' && <span className='active' />}
      </button>
      <button className='item' onClick={formatQuote}>
        <span className='icon quote' />
        <span className='text'>Quote</span>
        {blockType === 'quote' && <span className='active' />}
      </button>
      {codeStyle && (
        <button className='item' onClick={formatCode}>
          <span className='icon code' />
          <span className='text'>Code Block</span>
          {blockType === 'code' && <span className='active' />}
        </button>
      )}
    </div>
  );
}

interface ToolbarPluginConfig {
  codeButton?: boolean;
  alignButtons?: boolean;
  codeStyle?: boolean;
}

interface ToolbarPluginProps {
  disabled?: boolean;
  config?: ToolbarPluginConfig;
  className?: string;
}

export default function ToolbarPlugin({
  disabled = false,
  config = {
    codeButton: false,
    alignButtons: false,
    codeStyle: false,
  },
  className,
}: ToolbarPluginProps) {
  const classes = useToolbarStyles();
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState('paragraph');
  const [selectedElementKey, setSelectedElementKey] = useState<string | null>(
    null,
  );
  const [showBlockOptionsDropDown, setShowBlockOptionsDropDown] =
    useState(false);
  const [codeLanguage, setCodeLanguage] = useState('');
  const [isLink, setIsLink] = useState(false);
  const [showLinkEditor, setShowLinkEditor] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        setSelectedElementKey(elementKey);
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          setBlockType(type);
          if ($isCodeNode(element)) {
            setCodeLanguage(element.getLanguage() || getDefaultCodeLanguage());
          }
        }
      }
      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload) => {
          updateToolbar();
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, updateToolbar]);

  const codeLanguges = useMemo(() => getCodeLanguages(), []);
  const onCodeLanguageSelect = useCallback(
    (e) => {
      editor.update(() => {
        if (selectedElementKey !== null) {
          const node = $getNodeByKey(selectedElementKey);
          if ($isCodeNode(node)) {
            node.setLanguage(e.target.value);
          }
        }
      });
    },
    [editor, selectedElementKey],
  );

  const normalizedBlockType = KNOWN_BLOCK_TYPES.has(blockType)
    ? blockType
    : 'paragraph';

  const insertLink = useCallback(() => {
    setShowLinkEditor(true);
  }, []);

  const mobile = isMobile();

  return (
    <div className={cn(className, classes.toolbar)} ref={toolbarRef}>
      {!mobile && (
        <>
          <button
            disabled={!canUndo || disabled}
            onClick={() => {
              editor.dispatchCommand(UNDO_COMMAND, undefined);
            }}
            className='toolbar-item spaced'
            aria-label='Undo'
            type='button'>
            <i className='format undo' />
          </button>
          <button
            disabled={!canRedo || disabled}
            onClick={() => {
              editor.dispatchCommand(REDO_COMMAND, undefined);
            }}
            className='toolbar-item'
            aria-label='Redo'
            type='button'>
            <i className='format redo' />
          </button>
          <Divider />
        </>
      )}
      {!mobile && (
        <button
          disabled={disabled}
          className='toolbar-item block-controls'
          onClick={() => setShowBlockOptionsDropDown(!showBlockOptionsDropDown)}
          aria-label='Formatting Options'
          type='button'>
          <span className={'icon block-type ' + normalizedBlockType} />
          <span className='text'>
            {
              blockTypeToBlockName[
                normalizedBlockType as keyof typeof blockTypeToBlockName
              ]
            }
          </span>
          <i className='chevron-down' />
        </button>
      )}
      {!mobile &&
        showBlockOptionsDropDown &&
        createPortal(
          <BlockOptionsDropdownList
            editor={editor}
            blockType={normalizedBlockType}
            toolbarRef={toolbarRef}
            setShowBlockOptionsDropDown={setShowBlockOptionsDropDown}
            codeStyle={!!config.codeStyle}
          />,
          document.body,
        )}
      {!mobile && <Divider />}
      {blockType === 'code' ? (
        <>
          <Select
            disabled={disabled}
            className='toolbar-item code-language'
            onChange={onCodeLanguageSelect}
            options={codeLanguges}
            value={codeLanguage}
          />
          <i className='chevron-down inside' />
        </>
      ) : (
        <>
          <button
            disabled={disabled}
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            }}
            className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
            aria-label='Format Bold'
            type='button'>
            <i className='format bold' />
          </button>
          <button
            disabled={disabled}
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
            }}
            className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
            aria-label='Format Italics'
            type='button'>
            <i className='format italic' />
          </button>
          <button
            disabled={disabled}
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
            }}
            className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
            aria-label='Format Underline'
            type='button'>
            <i className='format underline' />
          </button>
          <button
            disabled={disabled}
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
            }}
            className={
              'toolbar-item spaced ' + (isStrikethrough ? 'active' : '')
            }
            aria-label='Format Strikethrough'
            type='button'>
            <i className='format strikethrough' />
          </button>
          {!!config.codeButton && (
            <button
              disabled={disabled}
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
              }}
              className={'toolbar-item spaced ' + (isCode ? 'active' : '')}
              aria-label='Insert Code'
              type='button'>
              <i className='format code' />
            </button>
          )}
          <button
            disabled={disabled}
            onClick={insertLink}
            className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
            aria-label='Insert Link'
            type='button'>
            <i className='format link' />
          </button>
          {showLinkEditor &&
            createPortal(
              <FloatingLinkEditor
                editor={editor}
                onRequestClose={() => setShowLinkEditor(false)}
              />,
              document.body,
            )}
          {!!config.alignButtons && (
            <>
              <Divider />
              <button
                disabled={disabled}
                onClick={() => {
                  editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
                }}
                className='toolbar-item spaced'
                aria-label='Left Align'
                type='button'>
                <i className='format left-align' />
              </button>
              <button
                disabled={disabled}
                onClick={() => {
                  editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
                }}
                className='toolbar-item spaced'
                aria-label='Center Align'
                type='button'>
                <i className='format center-align' />
              </button>
              <button
                disabled={disabled}
                onClick={() => {
                  editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
                }}
                className='toolbar-item spaced'
                aria-label='Right Align'
                type='button'>
                <i className='format right-align' />
              </button>
              <button
                disabled={disabled}
                onClick={() => {
                  editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
                }}
                className='toolbar-item'
                aria-label='Justify Align'
                type='button'>
                <i className='format justify-align' />
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
